/**
 * AuditLogsTab Component
 * Displays audit logs with filtering and export capabilities
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Search,
  Filter,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import {
  Card,
  Button,
  LoadingSpinner,
  EmptyState,
} from '../../../components/common';
import {
  getAuditLogs,
  getAuditStats,
  downloadAuditLogs,
} from '../../../services/audit.service';
import {
  AuditAction,
  AuditEntityType,
  AuditLogFilters,
  getActionLabel,
  getActionBadgeColor,
  getEntityTypeLabel,
} from '../../../types/audit';

export const AuditLogsTab: React.FC = () => {
  // Filters state
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
    startDate: subDays(new Date(), 30).toISOString().split('T')[0], // Last 30 days
  });

  const [searchInput, setSearchInput] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch audit logs
  const {
    data: logsData,
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => getAuditLogs(filters),
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['audit-stats', filters.startDate, filters.endDate],
    queryFn: () => getAuditStats(filters.startDate, filters.endDate),
  });

  // Handle search
  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchInput,
      offset: 0,
    }));
  };

  // Handle filter change
  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset to first page
    }));
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      await downloadAuditLogs(filters);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export audit logs');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle pagination
  const handleNextPage = () => {
    if (logsData && logsData.offset + logsData.limit < logsData.total) {
      setFilters((prev) => ({
        ...prev,
        offset: (prev.offset || 0) + (prev.limit || 50),
      }));
    }
  };

  const handlePrevPage = () => {
    if (filters.offset && filters.offset > 0) {
      setFilters((prev) => ({
        ...prev,
        offset: Math.max(0, (prev.offset || 0) - (prev.limit || 50)),
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className="text-3xl font-semibold text-stone-900 mb-2"
          style={{ fontFamily: 'Fraunces, Georgia, serif' }}
        >
          Audit Logs
        </h1>
        <p className="text-stone-600">
          Track all system activities and security events across the platform
        </p>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Total Events</p>
                  <p className="text-3xl font-semibold text-stone-900">
                    {stats?.totalEvents?.toLocaleString() ?? '0'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-primary-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Most Common Action</p>
                  <p className="text-lg font-medium text-stone-900">
                    {stats?.eventsByAction?.[0]
                      ? getActionLabel(stats.eventsByAction[0].action)
                      : 'N/A'}
                  </p>
                  <p className="text-sm text-stone-500">
                    {stats?.eventsByAction?.[0]
                      ? `${stats.eventsByAction[0].count} events`
                      : ''}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600 mb-1">Date Range</p>
                  <p className="text-lg font-medium text-stone-900">
                    {filters.startDate
                      ? `${format(new Date(filters.startDate), 'MMM dd, yyyy')}`
                      : 'All time'}
                  </p>
                  <p className="text-sm text-stone-500">
                    {filters.endDate
                      ? `to ${format(new Date(filters.endDate), 'MMM dd, yyyy')}`
                      : 'to now'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-secondary-600" />
                </div>
              </div>
            </Card>
          </div>
        )
      )}

      {/* Filters and Search */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Search and Actions Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  placeholder="Search by entity name, action, user email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <Button onClick={handleSearch} variant="secondary">
                Search
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </Button>
              <Button
                onClick={handleExport}
                disabled={isExporting}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-stone-200">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Action Type
                </label>
                <select
                  value={filters.action || ''}
                  onChange={(e) =>
                    handleFilterChange('action', e.target.value || undefined)
                  }
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Actions</option>
                  {Object.values(AuditAction).map((action) => (
                    <option key={action} value={action}>
                      {getActionLabel(action)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Entity Type
                </label>
                <select
                  value={filters.entityType || ''}
                  onChange={(e) =>
                    handleFilterChange('entityType', e.target.value || undefined)
                  }
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Types</option>
                  {Object.values(AuditEntityType).map((type) => (
                    <option key={type} value={type}>
                      {getEntityTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Audit Logs Table */}
      <Card className="overflow-hidden">
        {logsLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : logsError ? (
          <div className="p-8">
            <EmptyState
              title="Error Loading Audit Logs"
              description="There was a problem loading the audit logs. Please try again."
              action={{
                label: 'Retry',
                onClick: () => refetchLogs(),
              }}
            />
          </div>
        ) : !logsData || logsData.logs.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No Audit Logs Found"
              description="No audit events match your current filters."
              action={{
                label: 'Clear Filters',
                onClick: () =>
                  setFilters({
                    limit: 50,
                    offset: 0,
                  }),
              }}
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Entity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Tenant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-stone-200">
                  {logsData.logs.map((log) => (
                    <tr key={log.id} className="hover:bg-stone-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-900">
                        {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeColor(
                            log.action
                          )}`}
                        >
                          {getActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="text-stone-900">{log.entityName || 'N/A'}</div>
                        <div className="text-stone-500 text-xs">
                          {getEntityTypeLabel(log.entityType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {log.user ? (
                          <div>
                            <div className="text-stone-900">
                              {log.user.firstName} {log.user.lastName}
                            </div>
                            <div className="text-stone-500 text-xs">
                              {log.user.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-stone-400">System</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-900">
                        {log.tenant?.name || 'Platform'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                        {log.ipAddress || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between">
              <div className="text-sm text-stone-600">
                Showing {logsData.offset + 1} to{' '}
                {Math.min(logsData.offset + logsData.limit, logsData.total)} of{' '}
                {logsData.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePrevPage}
                  disabled={!filters.offset || filters.offset === 0}
                  variant="secondary"
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNextPage}
                  disabled={logsData.offset + logsData.limit >= logsData.total}
                  variant="secondary"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
